/**
 * Credit Account API (End Customer)
 *
 * APIs para end customers consultarem saldo e histórico de crédito.
 */

import api from './api'

// ============================================================================
// Types
// ============================================================================

export type CreditBalance = {
  has_credit_account: boolean
  balance: number
  currency: string
  credit_limit: number | null
  available_credit: number
  last_transaction_date: string | null
  low_balance_threshold: number
}

export type CreditTransaction = {
  id: string
  credit_account_id: string
  type: 'credit' | 'debit' | 'adjustment'
  amount: number
  balance_after: number
  currency: string
  description: string
  reference_type: string | null
  reference_id: string | null
  created_by: string
  created_at: string
  /** lançamento de contrato especial (#1091/#1107): o contrato e a competência */
  special_contract_id?: string | null
  competence_period?: string | null
}

export type CreditTransactionsResponse = {
  transactions: CreditTransaction[]
  total: number
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Consulta saldo de crédito do customer
 */
export const getCreditBalance = async (): Promise<CreditBalance> => {
  const response = await api.get('/api/v1/end-customer/credit/balance')
  return response.data
}

/**
 * Lista histórico de transações de crédito
 */
export const listCreditTransactions = async (params?: {
  skip?: number
  limit?: number
}): Promise<CreditTransaction[]> => {
  const response = await api.get('/api/v1/end-customer/credit/transactions', { params })
  return response.data.transactions || []
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formata valor monetário para exibição
 */
export const formatCurrency = (value: number, currency: string = 'BRL'): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value)
}

/**
 * Retorna label do tipo de transação
 */
export const getTransactionTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    credit: 'Crédito',
    debit: 'Débito',
    adjustment: 'Ajuste',
  }
  return labels[type] || type
}

/**
 * Retorna cor do badge do tipo de transação
 */
export const getTransactionTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    credit: 'bg-green-100 text-green-800',
    debit: 'bg-red-100 text-red-800',
    adjustment: 'bg-blue-100 text-blue-800',
  }
  return colors[type] || 'bg-gray-100 text-gray-800'
}

/**
 * Formata data ISO para exibição
 */
export const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

/**
 * Formata data e hora ISO para exibição
 */
export const formatDateTime = (isoDate: string): string => {
  const date = new Date(isoDate)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

// ============================================================================
// Export default object
// ============================================================================

const creditAccountApi = {
  getCreditBalance,
  listCreditTransactions,
  // Helper functions
  formatCurrency,
  getTransactionTypeLabel,
  getTransactionTypeColor,
  formatDate,
  formatDateTime,
}

export default creditAccountApi
